import { useEffect, useState } from 'react';
import type { FormikProps } from 'formik';
import { getIn } from 'formik';
import type { RoutesContextProps } from '@shared/core';
import { useTranslation } from '@shared/core';
import { useContactCollectorGuestTelemetry } from '../../ContactCollectorGuest.telemetry';
import { contactCollectorGuestRoutePaths } from '../..';
import type { ContactInputFields, Contacts, IframeContacts, IframeContactInputFields } from '../../shared/types';
import type { OptionType } from '@withjoy/joykit';
import { useFeatureValue, useIsFeatureEnabled } from '@shared/core/featureFlags';
import type { CountryData } from 'react-intl-tel-input';
import type { FieldVisibility } from './Form';
import { isUnitedStatesCode } from '@apps/card/utils';

import { e164Regex } from '../../shared/utils/validation';

export { e164Regex };

export const useFormController = (
  formik: FormikProps<Contacts> | FormikProps<IframeContacts>,
  isIframeVersion: boolean,
  e164PhoneNumber: { [key: number]: string },
  setE164PhoneNumber: React.Dispatch<React.SetStateAction<{ [key: number]: string }>>,
  coupleNames?: string,
  routeHelpers?: RoutesContextProps,
  phoneNumberRule: FieldVisibility = 'optional',
  mailingAddressRule: FieldVisibility = 'optional',
  disableAddressValidation: boolean = false,
) => {
  const [intlTelInputEnabled] = useIsFeatureEnabled('intlTelInputEnabled');
  const { value: contactCollectionInternationalAddressValidationEnabled } = useFeatureValue('contactCollectionInternationalAddressValidationEnabled');
  const { payload: contactCollectionDisablePhoneNumberValidationForFlagPayload = [] } = useFeatureValue('contactCollectionDisablePhoneNumberValidationFor');
  const { buttonInteracted } = useContactCollectorGuestTelemetry();
  const { t } = useTranslation('contactCollector');
  const tAdmin = t('admin');
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState<{ [key: number]: boolean }>({ 0: false });
  const [phoneNumberTouched, setPhoneNumberTouched] = useState<{ [key: number]: boolean }>({ 0: false });

  const isPhoneNumberInvalid = (index: number) => {
    // If phone number is hidden, it's never invalid
    if (phoneNumberRule === 'hidden') return false;
    // If phone number is required and empty, it's invalid
    if (phoneNumberRule === 'required' && phoneNumberTouched[index] && !formik.values.contacts[index].phoneNumber) return true;
    // Otherwise check the validation
    return !!formik.values.contacts[index].phoneNumber && phoneNumberTouched[index] && !isPhoneNumberValid[index];
  };

  const isPhoneNumberInvalidWithoutFocus = (index: number) => {
    // If phone number is hidden, it's never invalid
    if (phoneNumberRule === 'hidden') return false;
    // If phone number is required and empty, it's invalid (only after the field has been touched)
    if (phoneNumberRule === 'required' && phoneNumberTouched[index] && !formik.values.contacts[index].phoneNumber) return true;
    // Otherwise check the validation (no focus gate — IntlTelInputWrapper doesn't emit focus/blur)
    return !!formik.values.contacts[index].phoneNumber && !isPhoneNumberValid[index];
  };

  const isEmailInvalid = (index: number) => index === 0 && !formik.values.contacts[index].email && !!formik.touched.contacts?.[index].email;

  useEffect(() => {
    if (!coupleNames && !isIframeVersion && !!routeHelpers) {
      routeHelpers.goToExactRoute(contactCollectorGuestRoutePaths.landing.path);
    }
  }, [coupleNames, isIframeVersion, routeHelpers]);

  const getFormFieldError = (fieldName: keyof ContactInputFields | IframeContactInputFields, index: number) => {
    const { errors, touched } = formik;
    const name = `contacts[${index}][${fieldName}]`;
    const error = getIn(errors, name);
    const touch = getIn(touched, name);

    return touch && error ? error : undefined;
  };

  const handleOnCountryDropdownChange = (option: OptionType | null, index: number): void => {
    if (!option) return;

    formik.setFieldValue(`contacts[${index}].country`, option.label);
    formik.setFieldValue(`contacts[${index}].countryCode`, option.value);
  };

  const handleOnPhoneNumberChange = (isValid: boolean, value: string, selectedCountryData: CountryData, fullNumber: string, _extension: string, index: number): void => {
    const sanitizedValue = value.replace(/[^0-9+]/g, '');
    if (!e164Regex.test(sanitizedValue)) {
      return;
    }
    setIsPhoneNumberValid(prevState => ({
      ...prevState,
      [index]: isValid || (contactCollectionDisablePhoneNumberValidationForFlagPayload as string[])?.includes(selectedCountryData?.iso2 ?? ''),
    }));
    formik.setFieldValue(`contacts[${index}].phoneNumber`, sanitizedValue);
    // remove empty spaces and - to convert to e164 format
    setE164PhoneNumber(prevState => ({ ...prevState, [index]: fullNumber.replace(/ |-/g, '').trim() }));
  };

  const handleOnPhoneNumberFocus = (isValid: boolean, value: string, selectedCountryData: CountryData, fullNumber: string, extension: string, index: number): void => {
    if (!phoneNumberTouched[index]) {
      setPhoneNumberTouched(prevState => ({ ...prevState, [index]: true }));
    }
    handleOnPhoneNumberChange(isValid, value, selectedCountryData, fullNumber, extension, index);
  };

  const handleOnSaveDetailsClick = () => {
    // IntlTelInputWrapper already sets E.164 format directly into formik since we are using strictMode: true
    if (!intlTelInputEnabled) {
      Object.entries(e164PhoneNumber).forEach(element => element.length === 2 && formik.setFieldValue(`contacts[${element[0]}].phoneNumber`, element[1]));
    }
    buttonInteracted({ buttonType: 'save', element: 'saveDetails' });
    const noAddress = !formik.values.contacts[0].address1;
    const isMailingAddressHidden = mailingAddressRule === 'hidden';
    const isNationalAddress = isUnitedStatesCode(formik.values.contacts[0].countryCode);
    const skipAddressValidation = isMailingAddressHidden || (!isNationalAddress && !contactCollectionInternationalAddressValidationEnabled);
    if ((isIframeVersion && (noAddress || skipAddressValidation)) || disableAddressValidation) {
      return formik.handleSubmit();
    }
    if (routeHelpers) {
      routeHelpers.goToExactRoute(skipAddressValidation ? contactCollectorGuestRoutePaths.confirmation.path : contactCollectorGuestRoutePaths.address.path);
    }
    // else { consider putting a telemetry event here for address validation skipped }
  };

  return {
    tAdmin,
    handleOnSaveDetailsClick,
    isFormValid: formik.isValid,
    errors: {
      firstNameError: (index: number) => getFormFieldError('firstName', index),
      lastNameError: (index: number) => getFormFieldError('lastName', index),
      emailError: (index: number) => getFormFieldError('email', index),
      address1Error: (index: number) => getFormFieldError('address1', index),
      postalCodeError: (index: number) => getFormFieldError('postalCode', index),
      cityError: (index: number) => getFormFieldError('city', index),
      regionError: (index: number) => getFormFieldError('region', index),
      countryError: (index: number) => getFormFieldError('country', index),
    },
    handleOnCountryDropdownChange,
    handleOnPhoneNumberChange,
    handleOnPhoneNumberFocus,
    isPhoneNumberInvalid,
    isEmailInvalid,
    isPhoneNumberInvalidWithoutFocus,
    setIsPhoneNumberValid,
    phoneNumberTouched,
    setPhoneNumberTouched,
    intlTelInputEnabled,
  };
};
